import { Grid, Paper, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import {
  getDefaultOptionsForFormFields,
  IFilterObj
} from 'src/components/filters';
import FilterLoadingSkeleton from 'src/components/skeletons/filtersLoadingSkeleton';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { FormField2, IDynamicFormValue } from 'src/lib';
import { transformDynamicFormValueToFilterPayload } from 'src/components/filters/utils/filter-form-utils';
import { GridTableComponentV2, LocalFilters } from '../../../common';
import Legends from '../../../common/components/legends/legends';
import MODULE_TO_PAGE_MAP from '../../../home/types/simulation-module-to-page-map';
import { resetGridMetrics } from '../../store/as-is-to-be-slice';
import { getLocalFiltersData } from '../../store/simulator-summary-slice-async-thunks';
import { BarChartDetails } from '../../types/simulation-summary.model';
import { ColorData } from '../../utils/constants';
import { skeletonTemplate } from '../summary-bar-chart/bar-chart-container';
import useAsIsToBeHook from './use-as-is-to-be-v2.hook';
import useSummaryGridFilter from './use-summary-local-filter-v2.hook';
import {
  FiltersWrapper,
  setGlobalFilterFieldOptions,
  setGlobalFilterValues
} from '../../../common/features';
import TextInfo from '../summary-bar-chart/summary-text-component';
import PlannerDynamicToggle from '../../../planner_v2/components/planner-toggle/planner-toggle';
import { getPlannerToggles } from '../../../planner_v2/store/planner-slice-async-thunks';

interface IAsIsToBeProps {
  sourceId: number;
  moduleId: number;
  scenarioId: number;
  scenarioName: string;
  mode?: 'summary' | 'comparison'
}

export default function AsIsToBeGrid({
  moduleId,
  scenarioId,
  sourceId,
  scenarioName,
  mode = 'summary'
}: IAsIsToBeProps) {
  const appDispatch = useAppDispatch();
  const { moduleId: plannerModuleId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.PLANNER
  });
  const axisKeys = [
    {
      key: 'current',
      alias: 'Current',
      color: ColorData.greyColor
    },
    {
      key: 'new',
      alias: 'New',
      color: ColorData.primaryColor
    }
  ];
  const windowWidth = window.innerWidth - window.innerWidth * 0.1;
  const barWidth = windowWidth / 8;
  const { toggledView, toggles } = useAppSelector((state) => state.plannerV2);
  const gridStaticContent = toggles.find((toggle) => toggle.key === toggledView)
    ? toggles.find((toggle) => toggle.key === toggledView).alias
    : 'PPG';
  const isTargetEnabled = useAppSelector((state) => state.scenarioComparison.isTargetEnabled)

  const {
    asIs: asIsGrid,
    fetchingGridData,
    toBe: toBeGrid,
    barCharts,
    isFetchingBarCharts,
    dummyCharts,
    gridDataFetchWrapper,
    getAsIsBarChartData
  } = useAsIsToBeHook({ sourceId, moduleId, plannerModuleId, scenarioId, mode, isTargetEnabled });

  const { summary: summaryAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  const [isLocalFiltersReset, setIsLocalFiltersReset] = useState(false);
  const { toBeGridMetrics, localFilterValue } = useAppSelector(
    (state) => state.asIsToBeV2
  );

  const [currentGlobalFilterValue, setCurrentGlobalFilterValue] =
    useState<IDynamicFormValue>({});

  const {
    globalFilterCombinations,
    globalFilterFormFields,
    globalFilterInitialValues
  } = useAppSelector((state) => state.filters);
  const {
    fetchingLocalFiltersData,
    localFilterFormFields,
    localFilterInitialValues,
    onChange
  } = useSummaryGridFilter();

  let filter: IFilterObj[];
  let form: IDynamicFormValue;
  const handleGlobalFilterApply = (filters: IFilterObj[], isReset: boolean) => {
    if (isReset) {
      appDispatch(resetGridMetrics());
      appDispatch(setGlobalFilterValues(globalFilterInitialValues));
      appDispatch(
        setGlobalFilterFieldOptions(
          getDefaultOptionsForFormFields(
            globalFilterFormFields,
            globalFilterCombinations,
            globalFilterInitialValues
          )
        )
      );
      setIsLocalFiltersReset(true);

      appDispatch(
        getLocalFiltersData({
          sourceID: sourceId,
          scenarioId,
          filters
        })
      ).then((response) => {
        const { values, fields } = response.payload as {
          values: IDynamicFormValue;
          fields: FormField2[];
        };
        gridDataFetchWrapper({
          filters,
          localFilterFormFields,
          localFilterFormValue: values
        });
        return gridDataFetchWrapper({
          filters,
          localFilterFormFields,
          localFilterFormValue: values
        });
      });
    }

    appDispatch(
      getLocalFiltersData({
        sourceID: sourceId,
        scenarioId,
        filters
      })
    ).then((response) => {
      const { values, fields } = response.payload as {
        values: IDynamicFormValue;
        fields: FormField2[];
      };
      gridDataFetchWrapper({
        filters,
        localFilterFormFields,
        localFilterFormValue: values
      });
      return getAsIsBarChartData({
        filters,
        localFilterFormFields,
        localFilterFormValue: values
      });
    });
  };

  const handleLocalFilterApply = (filterValue: IDynamicFormValue) => {
    appDispatch(setGlobalFilterValues(currentGlobalFilterValue));
    const filters = transformDynamicFormValueToFilterPayload({
      combinations: globalFilterCombinations,
      formFields: globalFilterFormFields,
      formValue: currentGlobalFilterValue
    });
    gridDataFetchWrapper({
      filters,
      localFilterFormFields,
      localFilterFormValue: filterValue
    });
  };

  const onGlobalFilterFieldsFetched = (
    filters: IFilterObj[],
    formValue: IDynamicFormValue
  ): void => {
    setCurrentGlobalFilterValue(formValue);
    if (filters && Object.keys(formValue).length > 0) {
      appDispatch(
        getLocalFiltersData({
          sourceID: sourceId,
          scenarioId,
          filters
        })
      ).then((response) => {
        const { values, fields } = response.payload as {
          values: IDynamicFormValue;
          fields: FormField2[];
        };
        getAsIsBarChartData({
          filters,
          localFilterFormValue: values,
          localFilterFormFields: fields
        });
        return gridDataFetchWrapper({
          filters,
          localFilterFormValue: values,
          localFilterFormFields: fields
        });
      });
    }
  };

  useEffect(() => {
    onGlobalFilterFieldsFetched(filter, form);
  }, [isTargetEnabled])

  useEffect(() => {
    appDispatch(getPlannerToggles({ sourceId }));
  }, []);

  return (
    <Grid container>
      <FiltersWrapper
        accessConfig={summaryAccessConfig}
        externalReset={false}
        moduleId={plannerModuleId}
        onValueChange={(value) => {
          setCurrentGlobalFilterValue(value);
        }}
        onFilterFieldsFetched={onGlobalFilterFieldsFetched}
        onValueApply={handleGlobalFilterApply}
        scenarioId={scenarioId}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        setIsFilterValueChanged={() => { }}
        sourceId={sourceId}
      />
      <Grid item xs={12} my={1}>
        <Paper sx={{ p: 0 }} elevation={0}>
          {isFetchingBarCharts ? (
            skeletonTemplate({ dummyCharts, axisKeys, barWidth })
          ) : (
            <Grid
              container
              spacing={1}
              alignItems='center'
              sx={{ border: '1px solid #dcdcdc' }}
            >
              <Grid
                item
                xs={1.5}
                style={{
                  paddingLeft: '0px !important',
                  paddingTop: '0px !important'
                }}
              >
                <div
                  className='chart-legend-div'
                  style={{
                    paddingLeft: '8px',
                    marginTop: '-8px',
                    marginLeft: '-1px'
                  }}
                >
                  <div
                    className='chart-title-bold'
                    style={{ visibility: 'hidden' }}
                  >
                    Scenario {scenarioId}
                  </div>
                  <div>
                    <div className='legend-box' style={{ marginTop: '10px' }}>
                      <div className='current-icon'>&nbsp;</div>
                      {axisKeys[0].alias}
                    </div>
                    <div className='legend-box' style={{ marginTop: '10px' }}>
                      <div className='new-icon'>&nbsp;</div>
                      {axisKeys[1].alias}
                    </div>
                  </div>
                </div>
              </Grid>
              <Grid
                item
                xs={10.5}
                style={{
                  paddingLeft: '0px !important',
                  paddingTop: '0px !important'
                }}
              >
                <div
                  className='master-chart-div-summary'
                  style={{ justifyContent: 'space-between', marginTop: '-8px' }}
                >
                  {barCharts.map((chartData: BarChartDetails) => (
                    <div
                      className='chart-div'
                      style={{ minHeight: 'auto', width: `${100 / barCharts.length}%` }}
                      key={`chart_${chartData.metric}`}
                    >
                      <div style={{ marginTop: '5px' }} className='chart-title'>
                        {chartData.alias}
                      </div>
                      <div className='legend-div'>
                        <TextInfo
                          barWidthValue={barWidth}
                          barData={chartData}
                          height={60}
                          axisKeys={axisKeys}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Grid>
            </Grid>
          )}
        </Paper>
      </Grid>
      <Grid container item xs={12} alignItems='center'>
        <Grid item xs='auto'>
          <PlannerDynamicToggle />
        </Grid>
        <Grid item xs='auto' ml={2}>
          <Legends />
        </Grid>
      </Grid>
      <Grid
        container
        item
        xs={12}
        justifyContent='space-between'
        alignItems='flex-end'
      >
        <Grid item xs='auto'>
          {fetchingLocalFiltersData ? (
            FilterLoadingSkeleton(4)
          ) : (
            <LocalFilters
              formFields={localFilterFormFields}
              initialValues={localFilterInitialValues}
              isLocalFiltersReset={isLocalFiltersReset}
              onApply={handleLocalFilterApply}
              onChange={onChange}
            />
          )}
        </Grid>
        {toBeGridMetrics
          .filter(metric => metric.is_visible)
          .map(metric => {
            const sortedColors = [...metric.gradient_color_metadata]
              .sort((a, b) => a.order - b.order)
              .map(c => c.color)
              .join(', ') || '';
            return (
              <Grid item xs='auto' key={metric.label}>
                <Typography fontSize='10px' color='#3D3D3D'>
                  {metric.label}
                </Typography>
                <Grid
                  container
                  justifyContent='space-between'
                  sx={{
                    width: '250px',
                    height: '20px',
                    paddingX: '5px',
                    background: `linear-gradient(to right, ${sortedColors})`,
                  }}
                >
                  <Typography fontSize='10px' fontWeight={700}>
                    Min
                  </Typography>
                  <Typography fontSize='10px' fontWeight={700}>
                    Max
                  </Typography>
                </Grid>
              </Grid>
            );
          })}
      </Grid>
      <Grid
        item
        container
        justifyContent='center'
        xs={12}
        mt={1}
        sx={{ p: 2, backgroundColor: '#E5E5F5', border: '1px solid #ACACAC' }}
      >
        <Typography fontSize='14px' color='primary' fontWeight={700}>
          {`${scenarioName} `}
        </Typography>
        <Typography fontSize='14px' fontWeight={700}>
          {' '}
          - AS IS
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {toggledView ? (
          <GridTableComponentV2
            toggle={toggledView}
            setResponsiveHeight={false}
            headerRow={[
              `Click on ${gridStaticContent} to define opportunities`,
              ...asIsGrid.column_headers
            ]}
            fetchingData={fetchingGridData}
            gridData={asIsGrid}
          />
        ) : null}
      </Grid>
      <Grid
        item
        justifyContent='center'
        container
        xs={12}
        sx={{ p: 2, backgroundColor: '#E5E5F5', border: '1px solid #ACACAC' }}
      >
        <Typography fontSize='14px' color='primary' fontWeight={700}>
          {`${scenarioName} `}
        </Typography>
        <Typography fontSize='14px' fontWeight={700}>
          {' '}
          - TO BE
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {toggledView ? (
          <GridTableComponentV2
            toggle={toggledView}
            setResponsiveHeight={false}
            headerRow={[
              `Click on ${gridStaticContent} to define opportunities`,
              ...toBeGrid.column_headers
            ]}
            fetchingData={fetchingGridData}
            gridData={toBeGrid}
            toBeMetrics={toBeGridMetrics}
          />
        ) : null}
      </Grid>
    </Grid>
  );
}
